import { useQuery } from '@tanstack/react-query';
import {
  ProjectGroupByIdQuery,
  ProjectGroupByIdQueryVariables,
  ProjectGroupsQuery,
  ProjectGroupsQueryVariables,
  type ProjectGroupFilter,
} from '__generated__/graphql';
import classNames from 'classnames';
import GroupPicker, { IGroupPickerProps } from 'components/domain/pickers/GroupPicker/GroupPicker';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import useScrollerRef from 'hooks/ui/useScrollerRef';
import _ from 'lodash';
import { ClassName } from 'types/ClassName';
import { IProjectGroup } from '../../../../types/ProjectGroup';
import { projectGroupQuery, projectGroupsQuery } from './project-group-picker-queries';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { useOpenable } from 'hooks';
import { executeFunctionsCurried } from 'utils/functionUtils';

export interface IProjectGroupPickerProps
  extends Omit<IGroupPickerProps, 'data' | 'isLoading' | 'currentGroup' | 'dataError' | 'loadedAll'> {
  className?: ClassName;
  excludeIds?: IProjectGroup['id'][];
}

type ProjectGroupPickerGroup = NonNullArrayField<ProjectGroupsQuery['projectGroups']>;

export default function ProjectGroupPicker({
  value,
  excludeIds,
  className,
  onOpen,
  onClose,
  ...pickerProps
}: IProjectGroupPickerProps) {
  const graphqlClient = useGraphQLClient();

  const toggle = useOpenable();
  const { scroller, setScrollerRef } = useScrollerRef();

  const sectionSize = 10;
  const baseKey = useReactQueryBaseKey();

  const { data: selectedGroup } = useQuery({
    queryKey: [...baseKey, 'project-group-picker-by-id', value],
    queryFn: async () => {
      if (!value) {
        return null;
      }

      const result = await graphqlClient.request<ProjectGroupByIdQuery, ProjectGroupByIdQueryVariables>({
        document: projectGroupQuery,
        variables: { groupId: value },
      });

      return result.projectGroups?.[0]?.groupName ?? null;
    },
    enabled: !!value,
  });

  async function getProjectGroups(after: string | null, first: number): Promise<ProjectGroupPickerGroup[]> {
    const result = await graphqlClient.request<ProjectGroupsQuery, ProjectGroupsQueryVariables>({
      document: projectGroupsQuery,
      variables: {
        after,
        first,
        filter: getProjectGroupFilter(),
      },
    });

    return result.projectGroups ?? [];
  }

  const {
    data,
    isLoading,
    isError: isDataError,
    loadedAll,
  } = useReactQueryLazyLoading(
    scroller,
    [...baseKey, 'project-group-picker', { filter: getProjectGroupFilter() }],
    getProjectGroups,
    sectionSize,
    { enabled: toggle.isOpen }
  );

  function getProjectGroupFilter(): ProjectGroupFilter {
    const filter: ProjectGroupFilter = { deletedOn: { isNull: true } };

    if (excludeIds && excludeIds.length > 0) {
      filter.id = { doesNotContain: excludeIds };
    }

    return filter;
  }

  const classes = classNames('project-picker', className);

  return (
    <GroupPicker
      currentGroup={selectedGroup ?? null}
      className={classes}
      setScrollerRef={setScrollerRef}
      loadedAll={loadedAll}
      dataError={isDataError}
      data={data}
      isLoading={isLoading}
      value={value}
      onOpen={executeFunctionsCurried(toggle.open, onOpen)}
      onClose={executeFunctionsCurried(toggle.close, onClose)}
      {...pickerProps}
    />
  );
}
